import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { v4 } from 'uuid';
import {
  addToastNotification,
  deleteStatusNotification,
  setStatusNotification,
  updateStatusNotification,
  doBlinkLED
} from 'redux/actions';
import { Status, BLINK_DEVICE_LED_MIN_API_VERSION } from 'common/utils/constants';
import { IconLoader } from 'common/IconLoader';
import { SectorKPIsDialog, useSectorKPIs } from 'components/features/SectorKPIs';
import { LockUnlockGA2DeviceDialog, useLockUnlockGA2Device } from 'components/features/LockUnlockDevice';
import { ResetGainsDialog, useResetGains } from 'components/features/ResetGains';
import MaintenancePanelHeader from '../../MaintenancePanelHeader/MaintenancePanelHeader';
import MaintenancePanelTilt from '../../MaintenancePanelTilt/MaintenancePanelTilt';

const meetMinApiVersion = (apiVersion, minVersion) => {
  if (!apiVersion) return false;
  const version = parseInt(apiVersion.substring(1));
  return !isNaN(version) && version >= minVersion;
};

const isDeviceOnline = (device) => device?.connectionState?.toLowerCase() === 'connected';

function GA2ServiceMenubar({ source, targetDeviceType, targetDevice, handleUpdate, isGa2APIVersion = false}) {
  const dispatch = useDispatch();
  const handleLockUnlockDeviceMutate = useCallback(
    (variables) => {
      dispatch(
        setStatusNotification({
          id: 'serviceRequest',
          text:
            variables.serviceRequest === 'in-service-request'
              ? `Putting sector ${variables.rfPath} in service...`
              : `Putting sector ${variables.rfPath} out of service...`,
          status: Status.PENDING
        })
      );
    },
    [dispatch]
  );
  const handleLockUnlockDeviceSuccess = useCallback(
    (_, variables) => {
      dispatch(
        updateStatusNotification({
          id: 'serviceRequest',
          text:
            variables.serviceRequest === 'in-service-request'
              ? `Successfully put sector ${variables.rfPath} in service.`
              : `Successfully put sector ${variables.rfPath} out of service.`,
          status: Status.SUCCESS
        })
      );
      handleUpdate(variables.deviceId);
      setTimeout(() => {
        dispatch(deleteStatusNotification({ id: 'serviceRequest' }));
      }, 3000);
    },
    [dispatch, handleUpdate]
  );
  const handleLockUnlockDeviceError = useCallback(
    (_, variables) => {
      dispatch(
        updateStatusNotification({
          id: 'serviceRequest',
          text:
            variables.serviceRequest === 'in-service-request'
              ? `Failed to put sector ${variables.rfPath} in service.`
              : `Failed to put sector ${variables.rfPath} out of service.`,
          status: Status.FAILURE
        })
      );
    },
    [dispatch]
  );
  const lockUnlockGA2Device = useLockUnlockGA2Device(targetDevice, {
    onMutate: handleLockUnlockDeviceMutate,
    onSuccess: handleLockUnlockDeviceSuccess,
    onError: handleLockUnlockDeviceError
  });
  const handleSectorKPIsSuccess = useCallback(
    (_, variables) => {
      handleUpdate(variables.deviceId);
      dispatch(
        addToastNotification({
          id: v4(),
          title: variables.deviceId,
          description: 'has been synchronized.'
        })
      );
    },
    [dispatch, handleUpdate]
  );
  const handleSectorKPIsError = useCallback(
    (_, variables) => {
      dispatch(
        addToastNotification({
          id: v4(),
          title: variables.deviceId,
          description: 'Failed to synchronize',
          reverse: true
        })
      );
    },
    [dispatch]
  );
  const sectorKPIs = useSectorKPIs(targetDevice, {
    onSuccess: handleSectorKPIsSuccess,
    onError: handleSectorKPIsError
  });

  const handleResetGainsMutate = useCallback(
    (variables) => {
      dispatch(
        setStatusNotification({
          id: `resetGains-${variables.sector}`,
          text: `Clearing sector ${variables.sector}'s gain control parameters`,
          status: Status.PENDING
        })
      );
    },
    [dispatch]
  );

  const handleResetGainsSuccess = useCallback(
    (_, variables) => {
      dispatch(
        updateStatusNotification({
          id: `resetGains-${variables.sector}`,
          text: `Successfully cleared sector ${variables.sector}'s gain control parameters.`,
          status: Status.SUCCESS
        })
      );
      handleUpdate(variables.deviceId);
      setTimeout(() => {
        dispatch(deleteStatusNotification({ id: `resetGains-${variables.sector}` }));
      }, 3000);
    },
    [dispatch, handleUpdate]
  );
  const handleResetGainsError = useCallback(
    (_, variables) => {
      dispatch(
        updateStatusNotification({
          id: `resetGains-${variables.sector}`,
          text: `Failed to clear sector ${variables.sector}'s gain control parameters.`,
          status: Status.FAILURE
        })
      );
    },
    [dispatch]
  );
  const resetGains = useResetGains(targetDevice, {
    onMutate: handleResetGainsMutate,
    onSuccess: handleResetGainsSuccess,
    onError: handleResetGainsError
  });

  const handleBlinkLED = useCallback(() => {
    dispatch(setStatusNotification({ id: 'blink', text: 'Turning it on...', status: Status.PENDING }));
    
    const serialId = targetDevice?.id;
    const networkElement = targetDevice?.type;

    dispatch(
      doBlinkLED(
        targetDevice?.donorId,
        () => {
          // Start with red LED as first color
          dispatch(
            updateStatusNotification({
              id: 'blink',
              text: 'LED is blinking.',
              icon: IconLoader.led_status_red,
              status: Status.SUCCESS
            })
          );

          let isRed = true;
          const GA2_BLINK_DURATION = 10000; // 10 seconds
          const BLINK_INTERVAL = 250; // 250ms alternating pattern

          const blinkInterval = setInterval(() => {
            isRed = !isRed;
            console.log(isRed)
            dispatch(
              updateStatusNotification({
                id: 'blink',
                text: 'LED is blinking.',
                icon: isRed ? IconLoader.led_status_red : IconLoader.led_status_green,
                status: Status.SUCCESS
              })
            );
          }, BLINK_INTERVAL);

          setTimeout(() => {
            clearInterval(blinkInterval);
            handleUpdate(targetDevice?.deviceId);
            dispatch(deleteStatusNotification({ id: 'blink' }));
          }, GA2_BLINK_DURATION);
        },
        (error) => {
          dispatch(
            updateStatusNotification({
              id: 'blink',
              text: error?.message ? error.message : typeof error === 'string' ? error : '',
              status: Status.FAILURE
            })
          );
        },
        true, // isGa2
        networkElement,
        serialId
      )
    );
  }, [dispatch, targetDevice, handleUpdate]);

  const isBlinkDisabled = useCallback(() => {
    const isOnline = isDeviceOnline(targetDevice);
    return !isOnline || !isGa2APIVersion;
  }, [targetDevice]);

  return (
    <div id="maintenanceBar" role="menubar" aria-labelledby="maintenanceBarTitleLabel" className="maintenanceBar">
      <MaintenancePanelHeader targetDeviceType={targetDeviceType} source={source} />
      <div id="maintenanceBarRow" className="maintenanceBarRow">
        <MaintenancePanelTilt
          id={'maintenanceBarSectorKPIsTile'}
          disabled={sectorKPIs.trigger.disabled}
          selectionId={'sectorKPIs'}
          title={'Sector KPIs'}
          hoverMessage={sectorKPIs.tooltip.title ?? 'Sector KPIs'}
          handleMBarSelection={sectorKPIs.trigger.onClick}
        />
        <SectorKPIsDialog {...sectorKPIs.dialog} />
        <MaintenancePanelTilt
          id={'maintenanceBarServiceStateTile'}
          selectionId={'serviceState'}
          title={'In Service / Out of Service'}
          disabled={lockUnlockGA2Device.trigger.disabled}
          hoverMessage={lockUnlockGA2Device.tooltip.title ?? 'In Service / Out of Service'}
          handleMBarSelection={lockUnlockGA2Device.trigger.onClick}
        />
        <LockUnlockGA2DeviceDialog {...lockUnlockGA2Device.dialog} />
        <MaintenancePanelTilt
          id={'maintenanceBarResetGainsTile'}
          selectionId={'resetGains'}
          title={'Clear Gain Control'}
          disabled={resetGains.trigger.disabled}
          hoverMessage={resetGains.tooltip.title ?? 'Clear Gain Control'}
          handleMBarSelection={resetGains.trigger.onClick}
        />
        <ResetGainsDialog {...resetGains.dialog} />
        <MaintenancePanelTilt
          id={'maintenanceBarBlinkTile'}
          disabled={isBlinkDisabled()}
          selectionId={'blink'}
          title={'Blink LED'}
          handleMBarSelection={handleBlinkLED}
          hoverMessage={isBlinkDisabled() ? 'Device must be online and support LED blinking' : 'Blink LED'}
        />
      </div>
    </div>
  );
}

export default GA2ServiceMenubar;
